import { useMemo } from "react";
import Chart from "react-apexcharts";

const formatNumber = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "0";
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(numberValue);
};

const formatPercentage = (value, decimals = 1) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "0%";
  }

  return `${numberValue.toFixed(decimals)}%`;
};

function ProductionTrendByDepartmentChart({
  chartdata = {},
  chartKey = "",
  chartType = "column",
}) {
  const categories = useMemo(() => {
    return chartdata?.sortedDeptNames || [];
  }, [chartdata]);

  const series = useMemo(() => {
    return chartdata?.chartData || [];
  }, [chartdata]);

  const isAreaChart = chartType === "area";

  /**
   * Expected series order:
   *
   * 0 = Total Produced
   * 1 = OK Qty
   * 2 = Reject Qty
   * 3 = Rework Qty
   *
   * Percentage formula:
   *
   * selected series quantity / total produced quantity * 100
   */
  // NOTE: getPercentage is used only by options formatters below; define
  // it inside useMemo so it doesn't need to be a dependency of that hook.
  const options = useMemo(() => {
    const getPercentage = (value, dataPointIndex) => {
      const totalSeries = series?.[0]?.data || [];
      const total = Number(totalSeries?.[dataPointIndex]);
      const num = Number(value);

      if (!Number.isFinite(total) || total === 0 || !Number.isFinite(num)) {
        return 0;
      }

      return (num / total) * 100;
    };

    return {
      chart: {
        type: isAreaChart ? "area" : "bar",

        animations: {
          enabled: true,

          easing: "easeinout",

          speed: isAreaChart ? 1000 : 850,

          animateGradually: {
            enabled: true,
            delay: isAreaChart ? 120 : 90,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 500,
          },
        },

        toolbar: {
          show: false,
        },

        zoom: {
          enabled: false,
        },

        parentHeightOffset: 0,

        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },

      colors: [
        "#053c83",
        "#22c55e",
        "#ef4444",
        "#f59e0b",
      ],

      stroke: {
        show: true,

        curve: isAreaChart ? "smooth" : "straight",

        width: isAreaChart ? 3 : 0,

        lineCap: "round",
      },

      fill: isAreaChart
        ? {
            type: "gradient",

            gradient: {
              shade: "light",
              type: "vertical",

              shadeIntensity: 0.35,

              inverseColors: false,

              opacityFrom: 0.45,

              opacityTo: 0.03,

              stops: [0, 65, 100],
            },
          }
        : {
            opacity: 1,
          },

      plotOptions: {
        bar: {
          horizontal: false,

          columnWidth: "58%",

          borderRadius: 6,

          borderRadiusApplication: "end",

          borderRadiusWhenStacked: "last",

          // dataLabels: {
          //   position: "top",
          // },
        },
      },

      /**
       * Only show percentage above COLUMN chart.
       *
       * Area chart:
       * data labels completely disabled.
       */
      dataLabels: {
        enabled: false,

        formatter: (value, opts) => {
          const dataPointIndex = opts?.dataPointIndex;

          const percentage = getPercentage(
            value,
            dataPointIndex,
          );

          return formatPercentage(percentage, 1);
        },

        offsetY: -14,

        style: {
          fontSize: "10px",

          fontWeight: 700,

          colors: ["#334155"],
        },

        background: {
          enabled: false,
        },

        dropShadow: {
          enabled: false,
        },
      },

      markers: {
        size: isAreaChart ? 4 : 0,

        strokeWidth: 2,

        strokeColors: "#ffffff",

        hover: {
          size: 7,

          sizeOffset: 2,
        },
      },

      grid: {
        borderColor: "#e8edf5",

        strokeDashArray: 4,

        padding: {
          top: !isAreaChart ? 30 : 10,
          right: 10,
          bottom: 0,
          left: 5,
        },

        xaxis: {
          lines: {
            show: false,
          },
        },

        yaxis: {
          lines: {
            show: true,
          },
        },
      },

      xaxis: {
        categories,

        axisBorder: {
          show: false,
        },

        axisTicks: {
          show: false,
        },

        tooltip: {
          enabled: false,
        },

        labels: {
          rotate: -45,

          rotateAlways: categories.length > 8,

          trim: true,

          hideOverlappingLabels: true,

          style: {
            colors: "#64748b",

            fontSize: "10px",

            fontWeight: 500,
          },
        },
      },

      yaxis: {
        min: 0,

        forceNiceScale: true,

        labels: {
          formatter: (value) => formatNumber(value),

          style: {
            colors: "#64748b",

            fontSize: "10px",

            fontWeight: 500,
          },
        },
      },

      legend: {
        show: true,

        position: "top",

        horizontalAlign: "right",

        fontSize: "12px",

        fontWeight: 600,

        markers: {
          width: 8,

          height: 8,

          radius: 8,
        },

        itemMargin: {
          horizontal: 10,

          vertical: 4,
        },
      },

      tooltip: {
        shared: true,

        intersect: false,

        theme: "light",

        followCursor: false,

        marker: {
          show: true,
        },

        y: {
          formatter: (value, opts) => {
            const dataPointIndex = opts?.dataPointIndex;

            const percentage = getPercentage(
              value,
              dataPointIndex,
            );

            return `${formatNumber(value)} (${formatPercentage(
              percentage,
              2,
            )})`;
          },

          title: {
            formatter: (seriesName) => `${seriesName}: `,
          },
        },
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "lighten",

            value: 0.05,
          },
        },

        active: {
          allowMultipleDataPointsSelection: false,

          filter: {
            type: "darken",

            value: 0.08,
          },
        },
      },

      noData: {
        text: "No production data available",

        align: "center",

        verticalAlign: "middle",

        style: {
          color: "#64748b",

          fontSize: "13px",
        },
      },
    };
  }, [categories, isAreaChart, series]);

  if (!series.length) {
    return (
      <div className="chart-empty-state">
        No production trend data available.
      </div>
    );
  }

  return (
    <div
      className={`production-trend-chart-wrapper ${
        isAreaChart ? "area-view" : "column-view"
      }`}
    >
      <Chart
        key={`${chartKey}-${chartType}`}
        options={options}
        series={series}
        type={isAreaChart ? "area" : "bar"}
        height={400}
      />
    </div>
  );
}

export default ProductionTrendByDepartmentChart;